import {UniversalTable} from '@entities/components/universal-table/common.namespace';
import {
	Exclusivity,
	ExclusivityOption,
	GroupingMode,
	GroupOption,
	Inventory,
	InventoryExtra,
	RightsFranchise,
	RightsLanguageMetadata,
	RightsOption,
	RightsSourcesOption,
	RightsTablePatterns,
	RightsView,
	SortingFields,
	View,
	ViewOption,
} from '@entities/rights/rights.interfaces';
import {MasterType} from '@shared/enums/master-type.enum';
import {ExcelTableColumn} from '@shared/helpers/excel/excel.interfaces';
import {nameOf} from 'amc-common-components';
import {TimelineOptions} from 'vis-timeline/standalone';

/** Auxiliary 'RIGHTS_INVENTORY_ON_PAGE_ITEMS' variable.' */
export const RIGHTS_INVENTORY_ON_PAGE_ITEMS = 2500;

/** Auxiliary 'RIGHTS_VIEWS' variable.' */
export const RIGHTS_VIEWS: ViewOption[] = [
	{
		name: View.Table,
		icon: 'table',
	},
	{
		name: View.Timeline,
		icon: 'clock',
	},
];

/** Auxiliary 'RIGHTS_PAGE_SOURCES' variable.' */
export const RIGHTS_PAGE_SOURCES: RightsSourcesOption[] = [
	{
		name: 'RL/FT',
		id: RightsFranchise.RL,
	},
	{
		name: 'RLJE',
		id: RightsFranchise.RLJE,
	},
	{
		name: 'AMCN STUDIO',
		id: RightsFranchise.RLJE_STUDIO,
	},
	{
		name: 'HIDIVE',
		id: RightsFranchise.HIDIVE,
	},
];

/** Auxiliary 'RIGHTS_PAGE_GOLDEN_SOURCE' variable.' */
export const RIGHTS_PAGE_GOLDEN_SOURCE: RightsSourcesOption[] = [
	{
		name: 'GOLDEN',
		id: 'GOLDEN',
	},
];

/** Auxiliary 'RIGHTS_INVENTORY_GROUPING' variable.' */
export const RIGHTS_INVENTORY_GROUPING: GroupOption[] = [
	{
		name: GroupingMode.None,
	},
	{
		name: GroupingMode.Inventory,
		key: nameOf<Inventory>('content'),
	},
];

/** Auxiliary 'RIGHTS_PAGE_EXCLUSIVITY_OPTIONS' variable.' */
export const RIGHTS_PAGE_EXCLUSIVITY_OPTIONS: ExclusivityOption[] = [
	...Object.keys(Exclusivity).map((key) => ({
		name: Exclusivity[key],
		id: key,
	})),
];

/** Auxiliary 'RIGHTS_PAGE_RIGHTS_OPTIONS' variable.' */
export const RIGHTS_PAGE_RIGHTS_OPTIONS: RightsOption[] = [
	{
		name: 'Availability',
		id: RightsView.Availability,
	},
	{
		name: 'Rights In',
		id: RightsView.RightsIn,
	},
	{
		name: 'Rights Out',
		id: RightsView.RightsOut,
	},
	{
		name: 'Physical Inventory',
		id: RightsView.Physical,
	},
	{
		name: 'Schedule Availability',
		id: RightsView.Schedule,
	},
];

/** Auxiliary 'REMOVE_PINNED_NAME' variable.' */
export const REMOVE_PINNED_NAME = 'removePinned';
/** Auxiliary 'TABLE_ICON' variable.' */
export const TABLE_ICON = 'tableIcon';
/** Auxiliary 'FLAG_TOOLTIP_NAME' variable.' */
export const FLAG_TOOLTIP_NAME = 'flagTooltip';
/** Auxiliary 'RIGHTS_PINNED_COLUMNS' variable.' */
export const RIGHTS_PINNED_COLUMNS: (UniversalTable.BasicColumn | UniversalTable.PatternColumn<RightsTablePatterns>)[] =
	[
		{
			title: '',
			width: 'auto',
			type: UniversalTable.ColumnType.Pattern,
			id: REMOVE_PINNED_NAME,
			patternType: RightsTablePatterns.Icon,
		},
	];

/** Auxiliary 'RIGHTS_AVAILABILITY_TABLE_COLUMNS' variable.' */
export const RIGHTS_AVAILABILITY_TABLE_COLUMNS: (
	| UniversalTable.BasicColumn
	| UniversalTable.PatternColumn<RightsTablePatterns>
)[] = [
	{
		title: '',
		width: '2.5rem',
		type: UniversalTable.ColumnType.Pattern,
		id: nameOf<Inventory>('levelCode'),
		patternType: RightsTablePatterns.MasterTypeConst,
	},
	{
		title: 'Asset ID',
		width: '8rem',
		type: UniversalTable.ColumnType.String,
		id: nameOf<Inventory>('assetId'),
		sortField: SortingFields.objectCode,
		enableHtmlTooltip: true,
	},
	{
		title: 'Content Name',
		width: '4fr',
		type: UniversalTable.ColumnType.String,
		id: nameOf<Inventory>('content'),
		sortField: SortingFields.objectName,
		enableHtmlTooltip: true,
	},
	{
		title: '',
		width: '3.5rem',
		type: UniversalTable.ColumnType.Pattern,
		id: FLAG_TOOLTIP_NAME,
		patternType: RightsTablePatterns.Icon,
		enableHtmlTooltip: true,
	},
	{
		title: 'Start',
		width: '8rem',
		type: UniversalTable.ColumnType.Date,
		id: nameOf<Inventory>('dateStart'),
		sortField: SortingFields.actualTermStart,
		enableHtmlTooltip: true,
	},
	{
		title: 'End',
		width: '8rem',
		type: UniversalTable.ColumnType.Date,
		id: nameOf<Inventory>('dateEnd'),
		sortField: SortingFields.actualTermEnd,
		enableHtmlTooltip: true,
	},
	{
		title: 'Exclusivity',
		width: '8rem',
		type: UniversalTable.ColumnType.Pattern,
		id: nameOf<Inventory>('exclusivity'),
		sortField: SortingFields.exclusivityValue,
		patternType: RightsTablePatterns.ExclusivityConst,
	},
	{
		title: 'Territory',
		width: '3fr',
		type: UniversalTable.ColumnType.String,
		id: nameOf<Inventory>('territory'),
		sortField: SortingFields.territory,
		enableHtmlTooltip: true,
	},
	{
		title: 'Media',
		width: '5fr',
		type: UniversalTable.ColumnType.String,
		id: nameOf<Inventory>('media'),
		sortField: SortingFields.media,
		enableHtmlTooltip: true,
	},
	{
		title: 'Languages',
		width: '3fr',
		type: UniversalTable.ColumnType.String,
		id: nameOf<Inventory>('language'),
		sortField: SortingFields.language,
		enableHtmlTooltip: true,
	},
	{
		title: 'Channels',
		width: '3fr',
		type: UniversalTable.ColumnType.String,
		id: nameOf<Inventory>('channels'),
		sortField: SortingFields.channels,
		enableHtmlTooltip: true,
	},
	{
		title: 'Genres',
		width: '3fr',
		type: UniversalTable.ColumnType.String,
		id: nameOf<Inventory>('genres'),
		sortField: SortingFields.genres,
		enableHtmlTooltip: true,
	},
	{
		title: '',
		width: '3.5rem',
		type: UniversalTable.ColumnType.Pattern,
		id: nameOf<Inventory>('templateName'),
		patternType: RightsTablePatterns.Icon,
	},
	{
		title: '',
		width: '3.5rem',
		type: UniversalTable.ColumnType.Pattern,
		id: TABLE_ICON,
		patternType: RightsTablePatterns.Icon,
		enableHtmlTooltip: true,
	},
];

/** Auxiliary 'RIGHTS_IN_OUT_TABLE_COLUMNS' variable.' */
export const RIGHTS_IN_OUT_TABLE_COLUMNS: (
	| UniversalTable.BasicColumn
	| UniversalTable.PatternColumn<RightsTablePatterns>
)[] = [
	{
		title: '',
		width: 'auto',
		type: UniversalTable.ColumnType.Pattern,
		id: nameOf<Inventory>('levelCode'),
		patternType: RightsTablePatterns.MasterTypeConst,
	},
	{
		title: 'Deal ID',
		width: '8rem',
		type: UniversalTable.ColumnType.String,
		id: nameOf<Inventory>('dealId'),
	},
	{
		title: 'Deal Name',
		width: '3fr',
		type: UniversalTable.ColumnType.String,
		id: nameOf<Inventory>('dealName'),
	},
	{
		title: 'Licensee',
		width: '1.5fr',
		type: UniversalTable.ColumnType.String,
		id: nameOf<Inventory>('licensee'),
	},
	{
		title: 'Licensor',
		width: '1.05fr',
		type: UniversalTable.ColumnType.String,
		id: nameOf<Inventory>('licensor'),
	},
];

/** Auxiliary 'PHYSICAL_TABLE_COLUMNS' variable.' */
export const PHYSICAL_TABLE_COLUMNS: (
	| UniversalTable.BasicColumn
	| UniversalTable.PatternColumn<RightsTablePatterns>
)[] = [
	{
		title: '',
		width: '2.5rem',
		type: UniversalTable.ColumnType.Pattern,
		id: nameOf<Inventory>('levelCode'),
		patternType: RightsTablePatterns.MasterTypeConst,
	},
	{
		title: 'Media ID',
		width: '8rem',
		type: UniversalTable.ColumnType.String,
		id: nameOf<InventoryExtra>('inventoryAmcnId'),
	},
	{
		title: 'Media Title',
		width: '5fr',
		type: UniversalTable.ColumnType.String,
		id: nameOf<InventoryExtra>('inventoryTitle'),
	},
	{
		title: '',
		width: '3.5rem',
		type: UniversalTable.ColumnType.Pattern,
		id: FLAG_TOOLTIP_NAME,
		patternType: RightsTablePatterns.Icon,
		enableHtmlTooltip: true,
	},
	{
		title: 'Sell of End Date',
		width: '5fr',
		type: UniversalTable.ColumnType.Date,
		id: nameOf<InventoryExtra>('sellOfEndDate'),
	},
	{
		title: 'SKU',
		width: '5fr',
		type: UniversalTable.ColumnType.String,
		id: nameOf<InventoryExtra>('sku'),
	},
	{
		title: 'UPC',
		width: '3fr',
		type: UniversalTable.ColumnType.String,
		id: nameOf<InventoryExtra>('upc'),
	},
	{
		title: '',
		width: '3.5rem',
		type: UniversalTable.ColumnType.Pattern,
		id: nameOf<Inventory>('templateName'),
		patternType: RightsTablePatterns.Icon,
	},
];

/** Auxiliary 'RIGHTS_IN_TABLE_COLUMNS' variable.' */
export const RIGHTS_IN_TABLE_COLUMNS: (
	| UniversalTable.BasicColumn
	| UniversalTable.PatternColumn<RightsTablePatterns>
)[] = [
	{
		title: '',
		width: '2.5rem',
		type: UniversalTable.ColumnType.Pattern,
		id: nameOf<Inventory>('levelCode'),
		patternType: RightsTablePatterns.MasterTypeConst,
	},
	{
		title: 'Element Code',
		width: '1fr',
		type: UniversalTable.ColumnType.String,
		id: nameOf<InventoryExtra>('elementCode'),
	},
	{
		title: 'Element Name',
		width: '5fr',
		type: UniversalTable.ColumnType.String,
		id: nameOf<InventoryExtra>('elementName'),
	},
];

/** Auxiliary 'LANGUAGE_METADATA_TABLE_COLUMNS' variable.' */
export const LANGUAGE_METADATA_TABLE_COLUMNS: UniversalTable.BasicColumn[] = [
	{
		title: '',
		type: UniversalTable.ColumnType.String,
		id: nameOf<RightsLanguageMetadata>('LANGUAGE'),
	},
	{
		title: 'Sub',
		type: UniversalTable.ColumnType.String,
		id: nameOf<RightsLanguageMetadata>('SUB'),
	},
	{
		title: 'Dub',
		type: UniversalTable.ColumnType.String,
		id: nameOf<RightsLanguageMetadata>('DUB'),
	},
	{
		title: 'Metadata',
		type: UniversalTable.ColumnType.String,
		id: nameOf<RightsLanguageMetadata>('METADATA'),
	},
];

/** Auxiliary 'MAX_DATE' variable.' */
export const MAX_DATE = '2049-12-31';
/** Auxiliary 'MIN_DATE' variable.' */
export const MIN_DATE = '2000-01-01';

/** Auxiliary 'TemplateColors' variable.' */
export const TemplateColors = {
	[MasterType.Season]: '#1e8e4b',
	[MasterType.Movies]: '#a03c7d',
	[MasterType.Episode]: '#6a55be',
	[MasterType.Archived]: '#ff3232',
	[MasterType.Franchise]: '#187777',
	[MasterType.NonRL]: '#ff6f00',
	[MasterType.LiveStream]: '#ff6f00',
};

/** Auxiliary 'RIGHTS_TREE_SELECTORS' variable.' */
export const RIGHTS_TREE_SELECTORS = {
	language: 'language',
	channel: 'channel',
	genre: 'genre',
	mediaTypes: 'mediaTypes',
	territories: 'territories',
	contentCodes: 'contentCodes',
	projectCodes: 'projectCodes',
	productCodes: 'productCodes',
	workflowStatuses: 'workflowStatuses',
};

/** Auxiliary 'RIGHTS_PAGE_NO_VIRTUAL_SCROLL_ITEMS_LIMIT' variable.' */
export const RIGHTS_PAGE_NO_VIRTUAL_SCROLL_ITEMS_LIMIT = 30;

/** Auxiliary 'RIGHTS_PAGE_VS_THROTTLE' variable.' */
export const RIGHTS_PAGE_VS_THROTTLE = 300;
/** Auxiliary 'RIGHTS_PAGE_VS_SCROLL_DISTANCE' variable.' */
export const RIGHTS_PAGE_VS_SCROLL_DISTANCE = 1;
/** Auxiliary 'RIGHTS_PAGE_VS_SCROLL_UP_DISTANCE' variable.' */
export const RIGHTS_PAGE_VS_SCROLL_UP_DISTANCE = 2;
/** Auxiliary 'RIGHTS_INVENTORY_DEFAULT_GROUPING' variable.' */
export const RIGHTS_INVENTORY_DEFAULT_GROUPING = RIGHTS_INVENTORY_GROUPING[1].name;
/** Auxiliary 'RIGHTS_PAGE_DEFAULT_EXCLUSIVITY' variable.' */
export const RIGHTS_PAGE_DEFAULT_EXCLUSIVITY = RIGHTS_PAGE_EXCLUSIVITY_OPTIONS[0];
/** Auxiliary 'RIGHTS_PAGE_DEFAULT_RIGHTS' variable.' */
export const RIGHTS_PAGE_DEFAULT_RIGHTS = RIGHTS_PAGE_RIGHTS_OPTIONS[0];
/** Auxiliary 'RIGHTS_PAGE_DEFAULT_INVENTORY_VIEW' variable.' */
export const RIGHTS_PAGE_DEFAULT_INVENTORY_VIEW = View.Table;
/** Auxiliary 'RIGHTS_PAGE_DEFAULT_INVENTORY_EXPLICIT_MODE' variable.' */
export const RIGHTS_PAGE_DEFAULT_INVENTORY_EXPLICIT_MODE = false;

/** Auxiliary 'RIGHTS_PAGE_TIMELINE_OPTIONS' variable.' */
export const RIGHTS_PAGE_TIMELINE_OPTIONS: TimelineOptions = {
	stack: true,
	stackSubgroups: false,
	selectable: false,
	horizontalScroll: true,
	verticalScroll: true,
	zoomKey: 'ctrlKey',
	showTooltips: true,
	tooltip: {
		followMouse: false,
		overflowMethod: 'cap',
		delay: 0,
	},
	min: MIN_DATE,
	max: MAX_DATE,
	start: MIN_DATE,
	end: MAX_DATE,
	editable: false,
	margin: {
		item: 10, // minimal margin between items
		axis: 5, // minimal margin between items and the axis
	},
	orientation: 'top',
	autoResize: true,
	height: '100%',
	locale: 'en',
};

/** Auxiliary 'RightsColumnLabels' variable.' */
export const RightsColumnLabels: ExcelTableColumn[] = [
	{
		property: 'assetId',
		title: 'Asset Id',
		excelAlign: 'center',
		customWidth: 18,
		formatter: false,
	},
	{
		property: 'levelCode',
		title: 'Level Code',
		excelAlign: 'center',
		customWidth: 18,
		formatter: false,
	},
	{
		property: 'content',
		title: 'Content',
		excelAlign: 'center',
		customWidth: 64,
		formatter: false,
	},
	{
		property: 'dateStartObject',
		title: 'Date Start',
		excelAlign: 'center',
		customWidth: 18,
		formatter: false,
		numFmt: 'mm/dd/yyyy',
	},
	{
		property: 'dateEndObject',
		title: 'Date End',
		excelAlign: 'center',
		customWidth: 18,
		formatter: false,
		numFmt: 'mm/dd/yyyy',
	},
	{
		property: 'media',
		title: 'Media',
		excelAlign: 'left',
		customWidth: 18,
		formatter: false,
	},
	{
		property: 'territory',
		title: 'Territory',
		excelAlign: 'center',
		customWidth: 24,
		formatter: false,
	},
	{
		property: 'language',
		title: 'Language',
		excelAlign: 'center',
		customWidth: 18,
		formatter: false,
	},
	{
		property: 'linkIdsRL',
		title: 'Links Ids RL/FT',
		excelAlign: 'left',
		customWidth: 24,
		formatter: false,
	},
	{
		property: 'linkIdsRLJE',
		title: 'Links Ids RLJE',
		excelAlign: 'left',
		customWidth: 24,
		formatter: false,
	},
	{
		property: 'linkIdsAMCN',
		title: 'Links Ids AMCN Studio',
		excelAlign: 'left',
		customWidth: 24,
		formatter: false,
	},
	{
		property: 'exclusivity',
		title: 'Exclusivity',
		excelAlign: 'center',
		customWidth: 18,
		formatter: false,
	},
	{
		property: 'dealId',
		title: 'Deal Id',
		excelAlign: 'center',
		customWidth: 12,
		formatter: false,
		isDealColumn: true,
	},
	{
		property: 'dealName',
		title: 'Deal Name',
		excelAlign: 'left',
		customWidth: 48,
		formatter: false,
		isDealColumn: true,
	},
	{
		property: 'licensee',
		title: 'Licensee',
		excelAlign: 'left',
		customWidth: 26,
		formatter: false,
		isDealColumn: true,
	},
	{
		property: 'licensor',
		title: 'Licensor',
		excelAlign: 'left',
		customWidth: -1, // max
		formatter: false,
		isDealColumn: true,
	},
];
